const facetGroups = ['material', 'stone', 'size'];
const parameter = (group) => `cm_${group}`;
const materialTags = {
  'cm-material-stainless-steel': 'Stainless steel',
  'cm-material-sterling-silver': 'Sterling silver',
  'cm-material-copper': 'Copper',
  'cm-material-other-metal': 'Other metal',
};
const stoneTags = {
  'Lab-created moissanite': 'Moissanite',
  Moissanite: 'Moissanite',
  'Cubic zirconia': 'Cubic zirconia',
  Enamel: 'Enamel',
  'Natural stone': 'Natural stone',
  Glass: 'Glass',
  'No stones': 'No stones',
};
const collator = new Intl.Collator(document.documentElement.lang || 'en', { numeric: true });

class CantemiriCollectionTools extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('results-list');
    if (!this.root) return;
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
    this.observer = new MutationObserver((records) => {
      if (records.every((record) => record.target.closest?.('cantemiri-collection-tools, .cm-mobile-items-count, .cm-quick-empty'))) return;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.refresh());
    });
    this.observer.observe(this.root, { childList: true, subtree: true });
    this.refresh();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
  }

  onPopState = () => this.refresh();

  selection() {
    const url = new URL(window.location.href);
    const base = new URL(this.dataset.baseUrl, window.location.origin).pathname;
    const pathTags = decodeURIComponent(url.pathname.slice(base.length)).split(/[+/]/).filter(Boolean);
    const tags = [...(JSON.parse(this.dataset.currentTags || '[]') || []), ...pathTags];
    const selection = Object.fromEntries(facetGroups.map((group) => [group, url.searchParams.get(parameter(group)) || '']));
    for (const tag of tags) {
      if (!selection.material && materialTags[tag]) selection.material = materialTags[tag];
      const stone = stoneTags[tag] || Object.entries(stoneTags).find(([key]) => key.toLowerCase().replace(/[^a-z0-9]+/g, '-') === tag)?.[1];
      if (!selection.stone && stone) selection.stone = stone;
      if (!selection.size && tag.startsWith('cm-size-')) selection.size = tag.slice(8);
    }
    return selection;
  }

  refresh() {
    const source = this.root.querySelector('[data-cm-facet-products]');
    if (!source) return;
    let products;
    try { products = JSON.parse(source.textContent); } catch { return; }
    this.products = products;
    const selected = this.selection();
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has('cm_color')) {
      currentUrl.searchParams.delete('cm_color');
      history.replaceState(history.state, '', currentUrl.toString());
    }
    const currentSort = currentUrl.searchParams.get('sort_by') || 'best-selling';
    const quickSort = this.querySelector('[data-cm-sort]');
    if (quickSort) {
      quickSort.value = currentSort;
      const sortLabel = this.querySelector('[data-cm-sort-value]');
      if (sortLabel) sortLabel.textContent = quickSort.selectedOptions[0]?.textContent || '';
    }
    this.root.querySelectorAll('input[name="sort_by"]').forEach((input) => {
      input.checked = input.value === currentSort;
      const option = input.closest('[role="option"]');
      option?.setAttribute('aria-selected', String(input.checked));
    });
    if (this.source !== source.textContent) {
      this.source = source.textContent;
      const allTags = JSON.parse(this.dataset.allTags || '[]') || [];
      const choices = Object.fromEntries(facetGroups.map((group) => [group, new Set(products.flatMap((product) => product[group] || []).filter(Boolean))]));
      for (const tag of allTags) {
        if (materialTags[tag]) choices.material.add(materialTags[tag]);
        if (stoneTags[tag]) choices.stone.add(stoneTags[tag]);
        if (tag.startsWith('cm-size-')) choices.size.add(tag.slice(8));
      }
      for (const group of facetGroups) {
        const select = this.querySelector(`[data-cm-facet="${group}"]`);
        const field = select?.closest('[data-cm-group]');
        if (!select || !field) continue;
        const values = [...choices[group]].sort(collator.compare);
        field.hidden = values.length === 0;
        select.replaceChildren(new Option(select.dataset.emptyLabel, ''), ...values.map((value) => new Option(value, value)));
      }
      const visible = [...this.querySelectorAll('[data-cm-group]')].filter((field) => !field.hidden).length;
      this.querySelector('.cm-quick-filters').style.setProperty('--cm-quick-columns', String(Math.max(visible, 1)));
    }
    for (const group of facetGroups) {
      const select = this.querySelector(`[data-cm-facet="${group}"]`);
      if (!select) continue;
      if (selected[group] && ![...select.options].some((option) => option.value === selected[group])) select.add(new Option(selected[group], selected[group]));
      select.value = selected[group];
      const valueLabel = this.querySelector(`[data-cm-value="${group}"]`);
      if (valueLabel) valueLabel.textContent = select.selectedOptions[0]?.textContent || select.dataset.emptyLabel;
      select.toggleAttribute('data-active', Boolean(selected[group]));
    }
    const byId = new Map(products.map((product) => [String(product.id), product]));
    let visible = 0;
    this.root.querySelectorAll('.product-grid__item[data-product-id]').forEach((card) => {
      const product = byId.get(card.dataset.productId);
      const matches = product && facetGroups.every((group) => !selected[group] || (product[group] || []).includes(selected[group]));
      card.hidden = !matches;
      if (matches) visible += 1;
    });
    const label = (this.dataset.itemLabel || '[count]').replace('[count]', String(visible));
    this.root.querySelectorAll('.cm-mobile-items-count > span, .products-count-wrapper > span').forEach((node) => { if (node.textContent !== label) node.textContent = label; });
    const status = this.querySelector('[data-cm-status]');
    if (status && status.textContent !== label) status.textContent = label;
    const active = facetGroups.some((group) => selected[group]);
    this.querySelector('[data-cm-reset]').hidden = !active;
    let empty = this.root.querySelector('.cm-quick-empty');
    if (!empty) {
      empty = document.createElement('p');
      empty.className = 'cm-quick-empty';
      empty.textContent = this.dataset.emptyMessage;
      this.root.querySelector('#ResultsList')?.prepend(empty);
    }
    empty.hidden = visible > 0 || products.length === 0;
  }

  onChange = (event) => {
    const select = event.target.closest('select');
    if (!select) return;
    event.stopPropagation();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('cm_color');
    if (select.hasAttribute('data-cm-sort')) {
      url.searchParams.set('sort_by', select.value);
      window.location.assign(url.toString());
      return;
    }
    const group = select.dataset.cmFacet;
    if (!facetGroups.includes(group)) return;
    const selected = { ...this.selection(), [group]: select.value };
    for (const key of facetGroups) {
      if (selected[key]) url.searchParams.set(parameter(key), selected[key]);
      else url.searchParams.delete(parameter(key));
    }
    const base = new URL(this.dataset.baseUrl, window.location.origin);
    if (url.pathname.replace(/\/$/, '') !== base.pathname.replace(/\/$/, '')) {
      url.pathname = base.pathname;
      window.location.assign(url.toString());
      return;
    }
    history.pushState({}, '', url.toString());
    this.refresh();
  };

  onClick = (event) => {
    if (!event.target.closest('[data-cm-reset]')) return;
    event.preventDefault();
    const url = new URL(window.location.href);
    for (const key of facetGroups) url.searchParams.delete(parameter(key));
    for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.')) url.searchParams.delete(key);
    url.searchParams.delete('page');
    url.searchParams.delete('cm_color');
    const base = new URL(this.dataset.baseUrl, window.location.origin);
    if (url.pathname.replace(/\/$/, '') !== base.pathname.replace(/\/$/, '')) {
      url.pathname = base.pathname;
      window.location.assign(url.toString());
      return;
    }
    history.pushState({}, '', url.toString());
    this.refresh();
  };
}

if (!customElements.get('cantemiri-collection-tools')) customElements.define('cantemiri-collection-tools', CantemiriCollectionTools);
